import { rememberIdentifier } from "@kora/kit/entry/remembered-login";
import { TextField } from "@kora/kit/primitives/TextField/TextField";
import { useState } from "react";
import { ClientNotice, Failure, GhostButton, Loading, Messages, PasswordToggle, PrimaryButton, Screen } from "../components/Entry";
import { copy } from "../lib/copy";
import { errorsFor, go, hiddenErrors, loginChallenge, useFlow, valueOf } from "../lib/flow";
import { redirectFrom, verificationFrom, type SuccessBody } from "../lib/kratos";
import { href, navigate } from "../lib/router";

export function Registration({ query }: { query: URLSearchParams }) {
  const state = useFlow("registration", "/registration", query, {
    return_to: query.get("return_to") ?? undefined,
    login_challenge: query.get("login_challenge") ?? undefined,
    after_verification_return_to: query.get("after_verification_return_to") ?? undefined,
  });
  const { flow } = state;
  const [typed, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState(false);
  if (!flow) return state.failure ? <Screen title="Create account"><Failure text={state.failure} /></Screen> : <Loading />;

  const email = typed ?? (valueOf(flow, "traits.email") || query.get("email") || "");
  const challenge = loginChallenge(flow);
  const done = (body: SuccessBody) => {
    rememberIdentifier(email);
    const redirect = redirectFrom(body);
    // An OAuth sign-up goes straight back to the app; Kratos has already accepted the login with Hydra.
    if (challenge && redirect) return go(redirect);
    const verification = verificationFrom(body);
    if (verification) return navigate(href("/verification", { flow: verification, return_to: redirect || flow.return_to }));
    // Kratos has already emailed the link when sign-up doesn't hand over a verification flow.
    const address = body.identity?.verifiable_addresses?.find(a => !a.verified);
    if (address) return navigate(href("/verification", { email: address.value, sent: "1", return_to: redirect || flow.return_to }));
    go(redirect || flow.return_to || "/");
  };

  return (
    <Screen title="Create account">
      <ClientNotice client={flow.oauth2_login_request?.client} />
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          void state.submit(
            {
              method: "password",
              traits: { email: email.trim(), locale: navigator.language || "en-US" },
              password,
            },
            done,
          );
        }}
      >
        <TextField
          label="Email"
          inputType="email"
          inputProps={{ autoComplete: "email", name: "traits.email" }}
          value={email}
          onChange={setEmail}
          error={errorsFor(flow, "traits.email").map(copy)[0]}
          size="lg"
        />
        <TextField
          label="Password"
          inputType={shown ? "text" : "password"}
          inputProps={{ autoComplete: "new-password", name: "password" }}
          value={password}
          onChange={setPassword}
          error={errorsFor(flow, "password").map(copy)[0]}
          size="lg"
        />
        <PasswordToggle shown={shown} onToggle={() => setShown(!shown)} />
        <Messages messages={[...(flow.ui.messages ?? []), ...hiddenErrors(flow, ["traits.email", "password"])]} />
        <Failure text={state.failure} />
        <PrimaryButton pending={state.pending}>Create account</PrimaryButton>
        <GhostButton
          onClick={() =>
            navigate(href("/login", { return_to: flow.return_to, login_challenge: challenge, prompt: challenge ? "login" : undefined }))
          }
        >
          Back to sign in
        </GhostButton>
      </form>
    </Screen>
  );
}
